import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { UploadError } from '@shared/model';


@Component({
    selector: 'document-upload-select',
    templateUrl: './document-upload-select.component.html',
    styleUrls: ['./document-upload-select.component.scss'],
    standalone: false
})
export class DocumentUploadSelectComponent {
  @Input() multiple!: boolean;

  @Input() accept!: string;

  @Input() maxFileSize!: number;

  @Input() maxFileNameSize!: number;

  @Input() label?: string;

  @Output() selectFiles = new EventEmitter<File[]>();

  @Output() errorValidation = new EventEmitter<UploadError[]>();

  private files: File[] = [];
  private errors: UploadError[] = [];

  constructor(public readonly sanitizer: DomSanitizer) {
  }

  onFileSelect(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files: File[] = Array.from<File>(target.files as FileList);
    // Clear old errors
    this.errors = [];

    this.files = files.reduce((acc: File[], file: File) => {
      if (this.validate(file)) {
        if (this.isImage(file)) {
          // image preview
          (file as any).objectURL = this.sanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(file));
        }
        acc.push(file);
      }
      return acc;
    }, []);

    if (this.errors.length) {
      this.errorValidation.emit(this.errors);
      return;
    }

    this.selectFiles.emit(this.files);
  }

  validate(file: File): boolean {
    // File type check
    if (this.accept && !this.isFileTypeValid(file)) {
      this.errors.push({ type: 'accept', name: file.name });
      return false;
    }

    // File size check
    if (this.maxFileSize && file.size > this.maxFileSize) {
      this.errors.push({ type: 'fileSize', name: file.name });
      return false;
    }

    // File name size check
    if (this.maxFileNameSize && file.name.length > this.maxFileNameSize) {
      this.errors.push({ type: 'fileNameSize', name: file.name });
      return false;
    }

    return true;
  }

  isFileTypeValid(file: File): boolean {
    const acceptableTypes = this.accept.split(',').map(type => type.trim());

    for (const type of acceptableTypes) {
      const acceptable = this.isWildcard(type) ?
        this.getTypeClass(file.type) === this.getTypeClass(type) :
        file.type === type || this.getFileExtension(file).toLowerCase() === type.toLowerCase();

      if (acceptable) {
        return true;
      }
    }

    return false;
  }

  getTypeClass(fileType: string): string {
    return fileType.substring(0, fileType.indexOf('/'));
  }

  isWildcard(fileType: string): boolean {
    return fileType.indexOf('*') !== -1;
  }

  getFileExtension(file: File): string {
    return '.' + file.name.split('.').pop();
  }

  isImage(file: File): boolean {
    return /^image\//.test(file.type);
  }
}
